import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Separator } from "@/components/ui/separator";
import { useAccessProfile } from "@/hooks/use-admin-auth";
import { CRM_STALE_TIME } from "@/lib/crm/queries";
import {
	type NavGroup,
	type NavItem,
	visibleNavGroups,
} from "@/lib/nav-visibility";
import { cn } from "@/lib/utils";
import { orpc } from "@/utils/orpc";

interface AdminSidebarProps {
	collapsed: boolean;
}

/** Folded group keys, remembered per browser. */
const COLLAPSED_GROUPS_KEY = "admin-nav-collapsed-groups";
/** Queue counts refresh this often while the admin is open. */
const COUNTS_REFRESH_MS = 60_000;

const readCollapsed = (): string[] => {
	try {
		const saved: unknown = JSON.parse(
			localStorage.getItem(COLLAPSED_GROUPS_KEY) ?? "[]",
		);
		return Array.isArray(saved)
			? saved.filter((key): key is string => typeof key === "string")
			: [];
	} catch {
		return [];
	}
};

const writeCollapsed = (keys: string[]): void => {
	try {
		localStorage.setItem(COLLAPSED_GROUPS_KEY, JSON.stringify(keys));
	} catch {
		// Private mode etc.: folding just isn't remembered.
	}
};

const isActive = (item: NavItem, pathname: string): boolean =>
	[item.to, ...(item.matches ?? [])].some(
		(to) => pathname === to || (to !== "/" && pathname.startsWith(`${to}/`)),
	);

export function AdminSidebar({ collapsed }: AdminSidebarProps) {
	const { t } = useTranslation();
	const location = useLocation();
	const { profile } = useAccessProfile();
	const [folded, setFolded] = useState<string[]>(readCollapsed);

	// No nav until the profile loads — flashing the full nav at a sales-only
	// user (then yanking it) is worse than a moment of empty sidebar.
	const groups = profile ? visibleNavGroups(profile) : [];

	const { data: myTasks } = useQuery({
		...orpc.admin.crm.tasks.mine.queryOptions(),
		enabled: !!profile?.canManageCrm,
		staleTime: CRM_STALE_TIME,
	});
	const { data: queues } = useQuery({
		...orpc.admin.navCounts.queryOptions(),
		enabled: !!profile?.canProvideSupport,
		refetchInterval: COUNTS_REFRESH_MS,
	});
	const badgeCount = (item: NavItem): number | null => {
		switch (item.badge) {
			case "openTasks":
				return myTasks?.tasks.length ?? null;
			case "accessRequests":
			case "billingIssues":
			case "supportRequests":
				return queues?.[item.badge] ?? null;
			default:
				return null;
		}
	};

	// Opening a page in a folded group unfolds it.
	const activeGroup = groups.find((group) =>
		group.items.some((item) => isActive(item, location.pathname)),
	)?.key;
	useEffect(() => {
		if (!activeGroup) return;
		setFolded((current) => {
			if (!current.includes(activeGroup)) return current;
			const next = current.filter((key) => key !== activeGroup);
			writeCollapsed(next);
			return next;
		});
	}, [activeGroup]);

	const toggle = (key: string): void => {
		setFolded((current) => {
			const next = current.includes(key)
				? current.filter((value) => value !== key)
				: [...current, key];
			writeCollapsed(next);
			return next;
		});
	};

	const renderNavItem = (item: NavItem) => {
		// Detail pages keep their section highlighted (/crm/contacts/<id>).
		const active = isActive(item, location.pathname);
		const count = badgeCount(item);
		return (
			<Link
				key={item.to}
				to={item.to}
				aria-current={active ? "page" : undefined}
				title={collapsed ? t(item.labelKey) : undefined}
				className={cn(
					"flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
					active
						? "bg-primary text-primary-foreground"
						: "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
				)}
			>
				<item.icon className="h-5 w-5 flex-shrink-0" />
				{!collapsed && <span className="flex-1">{t(item.labelKey)}</span>}
				{!collapsed && count !== null && count > 0 && (
					<span
						className={cn(
							"rounded-full px-2 py-0.5 font-medium text-xs",
							active
								? "bg-primary-foreground/20"
								: "bg-primary/10 text-primary",
						)}
						data-testid={`nav-badge-${item.badge}`}
					>
						{count}
					</span>
				)}
			</Link>
		);
	};

	const renderGroup = (group: NavGroup) => {
		// The icon-only sidebar has no room for headings: every item shows.
		if (!group.labelKey || collapsed) return group.items.map(renderNavItem);
		const open = !folded.includes(group.key);
		// A folded group still shows how much is waiting in it.
		const waiting = open
			? 0
			: group.items.reduce((sum, item) => sum + (badgeCount(item) ?? 0), 0);
		return (
			<>
				<button
					type="button"
					aria-expanded={open}
					onClick={() => toggle(group.key)}
					className="flex w-full items-center gap-2 rounded-md px-3 py-1 font-semibold text-muted-foreground text-xs uppercase tracking-wide hover:text-foreground"
				>
					<span className="flex-1 text-left">{t(group.labelKey)}</span>
					{waiting > 0 && (
						<span className="rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary normal-case tracking-normal">
							{waiting}
						</span>
					)}
					<ChevronDown
						className={cn(
							"h-4 w-4 transition-transform",
							!open && "-rotate-90",
						)}
					/>
				</button>
				{open && group.items.map(renderNavItem)}
			</>
		);
	};

	return (
		<aside
			className={cn(
				"flex h-full flex-col border-r bg-muted/40 transition-all duration-300",
				collapsed ? "w-16" : "w-64",
			)}
		>
			<div className="flex h-14 items-center border-b px-6">
				{!collapsed && (
					<h1 className="font-semibold text-lg">{t("common:app_name")}</h1>
				)}
			</div>
			<nav className="flex-1 space-y-1 overflow-y-auto p-2">
				{groups.map((group, index) => (
					<Fragment key={group.key}>
						{index > 0 && <Separator className="my-2" />}
						{renderGroup(group)}
					</Fragment>
				))}
			</nav>
		</aside>
	);
}
